import React, { useState, useEffect, useCallback } from 'react';
import { UncontrolledTooltip, Row, Col, Nav, NavItem, NavLink, FormGroup, Label, Input, Button } from 'reactstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import classnames from 'classnames';
import { debounce } from 'lodash';
import "react-datepicker/dist/react-datepicker.css";
import { TARGET_AUDIENCE_TYPES, PROGRAM_TYPES, TARGET_OPTIONS } from './constants';
import NotificationsService from '../../../../services/NotificationsService';
import UsersService from '../../../../services/UsersService';
import SegmentsService from '../../../../services/SegmentsService';
import ProgramsService from '../../../../services/ProgramsService';
import HealthProgramService from '../../../../services/HealthProgramService';
import ToastService from '../../../../services/ToastService';
import Icon from "../../../../base/components/Icon";
import joinClassNames from "../../../../base/helpers/joinClassNames";
import styles from './CustomNotifications.module.scss';

export default function Custom() {
    const [activeTab, setActiveTab] = useState(TARGET_AUDIENCE_TYPES.ALL);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        target: null,
        userSegments: [],
        selectedUsers: [],
        programType: null,
        program: null,
        sendImmediately: false,
        sendAt: new Date()
    });
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [userSegments, setUserSegments] = useState([]);
    const [users, setUsers] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [loadingSegments, setLoadingSegments] = useState(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingPrograms, setLoadingPrograms] = useState(false);

    const toastService = new ToastService();

    const formatSegmentName = (name) => {
        if (!name) return '';

        return name
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    useEffect(() => {
        const fetchUserSegments = async () => {
            setLoadingSegments(true);
            try {
                const segmentsService = new SegmentsService();
                const response = await segmentsService.getSegments();
                const segmentOptions = response.data.map(segment => ({
                    value: segment.id,
                    label: formatSegmentName(segment.fullName || segment.name)
                }));
                setUserSegments(segmentOptions);
            } catch (error) {
                console.error('Error fetching user segments:', error);
            } finally {
                setLoadingSegments(false);
            }
        };

        fetchUserSegments();
    }, []);

    const fetchUsers = async (searchQuery = '') => {
        if (!searchQuery || searchQuery.length < 2) {
            setUsers([]);
            return;
        }

        setLoadingUsers(true);
        try {
            const usersService = new UsersService();
            const response = await usersService.getUsers({
                limit: 100,
                offset: 0,
                q: searchQuery
            });

            const userOptions = response.data.map(user => ({
                value: user.id,
                label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
                email: user.email
            }));
            setUsers(userOptions);
        } catch (error) {
            console.error('Error fetching users:', error);
            setUsers([]);
        } finally {
            setLoadingUsers(false);
        }
    };


    const debouncedUserSearch = useCallback(
        debounce((searchTerm) => {
            fetchUsers(searchTerm);
        }, 500),
        []
    );

    const handleUserInputChange = (inputValue) => {
        setUserSearchTerm(inputValue);
        debouncedUserSearch(inputValue);
    };

    useEffect(() => {
        const fetchPrograms = async () => {
            if (!formData.programType) {
                setPrograms([]);
                return;
            }
            setPrograms([]);
            setFormData(prev => ({ ...prev, program: null }));
            setLoadingPrograms(true);
            try {
                let response;

                switch (formData.programType) {
                    case PROGRAM_TYPES.NUTRITION:
                        {
                            const programsService = new ProgramsService();
                            response = await programsService.getPrograms({ limit: 100 });
                            break;
                        }
                    case PROGRAM_TYPES.FITNESS:
                        {
                            const healthProgramService = new HealthProgramService();
                            response = await healthProgramService.getWellnessPrograms({
                                wellnessType: 1,
                                isPredefined: false,
                                limit: 100
                            });
                            break;
                        }
                    case PROGRAM_TYPES.WELLNESS:
                        {
                            const healthProgramServiceWellness = new HealthProgramService();
                            response = await healthProgramServiceWellness.getWellnessPrograms({
                                wellnessType: 2,
                                isPredefined: false,
                                limit: 100
                            });
                            break;
                        }
                    default:
                        setPrograms([]);
                        return;
                }

                const programOptions = response.data.map(program => ({
                    value: program.id,
                    label: program.fullName || program.name || `Program ${program.id}`,
                    programData: program
                }));

                setPrograms(programOptions);
            } catch (error) {
                console.error('Error fetching programs:', error);
                setPrograms([]);
            } finally {
                setLoadingPrograms(false);
            }

            setFormData(prev => ({ ...prev, program: null }));
        };

        fetchPrograms();
    }, [formData.programType]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setFormData(prev => ({
            ...prev,
            userSegments: [],
            selectedUsers: [],
            programType: null,
            program: null
        }));
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.title || !formData.message || !formData.target) {
            toastService.error('Please fill in all required fields');
            return;
        }

        if (formData.title.length > 50) {
            toastService.error('Title must be 50 characters or less');
            return;
        }

        if (formData.message.length > 100) {
            toastService.error('Message must be 100 characters or less');
            return;
        }

        if (activeTab === TARGET_AUDIENCE_TYPES.SEGMENT && formData.userSegments.length === 0) {
            toastService.error('Please select at least one user segment');
            return;
        }

        if (activeTab === TARGET_AUDIENCE_TYPES.MANUAL && formData.selectedUsers.length === 0) {
            toastService.error('Please select at least one user');
            return;
        }

        if (activeTab === TARGET_AUDIENCE_TYPES.PROGRAM && (!formData.programType || !formData.program)) {
            toastService.error('Please select program type and program');
            return;
        }

        const payload = {
            title: formData.title,
            message: formData.message,
            target: formData.target.value,
            ...(formData.sendImmediately ? {} : { sendAt: formData.sendAt.toISOString() })
        };

        switch (activeTab) {
            case TARGET_AUDIENCE_TYPES.ALL:
                payload.toAll = true;
                break;
            case TARGET_AUDIENCE_TYPES.SEGMENT:
                payload.userSegments = formData.userSegments.map(segment => segment.value);
                break;
            case TARGET_AUDIENCE_TYPES.MANUAL:
                payload.emails = formData.selectedUsers.map(user => user.email);
                break;
            case TARGET_AUDIENCE_TYPES.PROGRAM: {
                const selectedProgram = programs.find(p => p.value === formData.program.value);
                if (selectedProgram && selectedProgram.programData) {
                    payload.program = {
                        id: selectedProgram.programData.id,
                        type: formData.programType
                    };
                }
                break;
            }
            default:
                break;
        }

        try {
            setIsLoading(true);
            const notificationsService = new NotificationsService();
            const response = await notificationsService.sendCustomNotification(payload);

            if (formData.sendImmediately) {
                toastService.success('Notification sent successfully!');
            } else {
                toastService.success('Your notification has been successfully scheduled.');
            }

            setFormData({
                title: '',
                message: '',
                target: null,
                userSegments: [],
                selectedUsers: [],
                programType: null,
                program: null,
                sendImmediately: false,
                sendAt: new Date()
            });
        } catch (error) {
            let errorMessage = 'Failed to send notification. Please try again.';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.response?.data?.error) {
                errorMessage = error.response.data.error;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            toastService.error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const formatUserOption = (user) => (
        <div>
            <strong>{user.label}</strong> ({user.email})
        </div>
    );

    const renderInfoTooltip = (id, text) => (
        <>
            <Icon
                icon="infoCircle"
                className="ms-2"
                id={id}
            />
            <UncontrolledTooltip
                popperClassName={joinClassNames(
                    "tooltip-alternative-name error-result-tooltip"
                )}
                innerClassName="pre-line text-truncate error-inner-max-height text-start"
                placement="bottom"
                target={id}
            >
                {text}
            </UncontrolledTooltip>
        </>
    );

    return (
        <Row>
            <Col className="mt-3" lg={12}>
                <div className="mb-4">
                    <h3 className="mb-2">Target audience</h3>
                    <Nav pills className={`mb-3 ${styles.navPills}`}>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === TARGET_AUDIENCE_TYPES.ALL }, styles.navLink)}
                                onClick={() => handleTabChange(TARGET_AUDIENCE_TYPES.ALL)}
                            >
                                All users
                                {renderInfoTooltip('all-users-info', 'Notification will be sent to all the active users who use the app')}
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === TARGET_AUDIENCE_TYPES.SEGMENT }, styles.navLink)}
                                onClick={() => handleTabChange(TARGET_AUDIENCE_TYPES.SEGMENT)}
                            >
                                By User Segment
                                {renderInfoTooltip('user-segment-info', 'Notification will be sent to users who fall into specific one or many User Segments. For example, people who prefer the Mediterranean diet based on a questionnaire.')}
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === TARGET_AUDIENCE_TYPES.MANUAL }, styles.navLink)}
                                onClick={() => handleTabChange(TARGET_AUDIENCE_TYPES.MANUAL)}
                            >
                                Manual selection
                                {renderInfoTooltip('manual-selection-info', 'Notification will be sent to a specific customer(s).')}
                            </NavLink>
                        </NavItem>
                        <NavItem>
                            <NavLink
                                className={classnames({ active: activeTab === TARGET_AUDIENCE_TYPES.PROGRAM }, styles.navLinkLast)}
                                onClick={() => handleTabChange(TARGET_AUDIENCE_TYPES.PROGRAM)}
                            >
                                Program users
                                {renderInfoTooltip('program-users-info', 'Notification will be sent to all customers who follow the specific Program.')}
                            </NavLink>
                        </NavItem>
                    </Nav>

                    <div className={styles.contentSection}>
                        {activeTab === TARGET_AUDIENCE_TYPES.SEGMENT && (
                            <FormGroup>
                                <Label>User Segment(s)</Label>
                                <Select
                                    isMulti
                                    options={userSegments}
                                    value={formData.userSegments}
                                    onChange={(selected) => handleInputChange('userSegments', selected || [])}
                                    placeholder="Select user segments..."
                                    isLoading={loadingSegments}
                                    noOptionsMessage={() => loadingSegments ? "Loading segments..." : "No segments found"}
                                />
                            </FormGroup>
                        )}

                        {activeTab === TARGET_AUDIENCE_TYPES.MANUAL && (
                            <FormGroup>
                                <Label>User(s)</Label>
                                <Select
                                    isMulti
                                    options={users}
                                    value={formData.selectedUsers}
                                    onChange={(selected) => {
                                        if (selected && selected.length <= 50) {
                                            handleInputChange('selectedUsers', selected);
                                        }
                                    }}
                                    onInputChange={handleUserInputChange}
                                    placeholder="Type at least 2 characters to search users..."
                                    formatOptionLabel={formatUserOption}
                                    isOptionDisabled={() => formData.selectedUsers.length >= 50}
                                    isLoading={loadingUsers}
                                    noOptionsMessage={({ inputValue }) => {
                                        if (!inputValue || inputValue.length < 2) {
                                            return "Type at least 2 characters to search";
                                        }
                                        return loadingUsers ? "Searching users..." : "No users found";
                                    }}
                                    filterOption={null}
                                    cacheOptions={false}
                                />
                                <small className="text-muted">
                                    Maximum 50 users allowed. Type at least 2 characters to search users by name or email.
                                </small>
                            </FormGroup>
                        )}

                        {activeTab === TARGET_AUDIENCE_TYPES.PROGRAM && (
                            <>
                                <FormGroup>
                                    <Label>Program type</Label>
                                    <Select
                                        options={[
                                            { value: PROGRAM_TYPES.NUTRITION, label: 'Nutrition' },
                                            { value: PROGRAM_TYPES.FITNESS, label: 'Fitness' },
                                            { value: PROGRAM_TYPES.WELLNESS, label: 'Wellness' }
                                        ]}
                                        value={formData.programType ? {
                                            value: formData.programType,
                                            label: formData.programType === PROGRAM_TYPES.NUTRITION ? 'Nutrition' :
                                                formData.programType === PROGRAM_TYPES.FITNESS ? 'Fitness' : 'Wellness'
                                        } : null}
                                        onChange={(selected) => handleInputChange('programType', selected?.value || null)}
                                        placeholder="Select program type..."
                                    />
                                </FormGroup>

                                {formData.programType && (
                                    <FormGroup>
                                        <Label>Program</Label>
                                        <Select
                                            options={programs}
                                            value={formData.program}
                                            onChange={(selected) => handleInputChange('program', selected)}
                                            placeholder="Select program..."
                                            isLoading={loadingPrograms}
                                            noOptionsMessage={() => loadingPrograms ? "Loading programs..." : "No programs found"}
                                        />
                                    </FormGroup>
                                )}
                            </>
                        )}
                    </div>
                </div>

                <div className="mb-1">
                    <h3 className="mb-2">Content</h3>
                    <div className={styles.contentSection}>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label>Title <span className="text-danger">*</span></Label>
                                    <Input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => handleInputChange('title', e.target.value)}
                                        maxLength={50}
                                        placeholder="Enter notification title..."
                                    />
                                    <small className="text-muted">{formData.title.length}/50 characters</small>
                                </FormGroup>
                            </Col>
                        </Row>

                        <FormGroup>
                            <Label>Message <span className="text-danger">*</span></Label>
                            <Input
                                type="textarea"
                                rows="3"
                                value={formData.message}
                                onChange={(e) => handleInputChange('message', e.target.value)}
                                maxLength={100}
                                placeholder="Enter notification message..."
                            />
                            <small className="text-muted">{formData.message.length}/100 characters</small>
                        </FormGroup>

                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label className="d-flex align-items-center">Target <span className="text-danger">*</span>
                                        {renderInfoTooltip('target-info', 'Specify the list of recipients by choosing one of the options')}
                                    </Label>
                                    <Select
                                        options={TARGET_OPTIONS}
                                        value={formData.target}
                                        onChange={(selected) => handleInputChange('target', selected)}
                                        placeholder="Select target..."
                                    />
                                    <small className="text-muted">Pick a target to let app know where to redirect user once he clicks on push notification</small>
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>
                </div>

                <div className="mb-4">
                    <h3 className="mb-2">Time</h3>
                    <div className={styles.contentSection}>

                        <FormGroup className="mb-3">
                            <div className="d-flex align-items-center">
                                <Label className="me-3 mb-0">Send immediately</Label>
                                <div
                                    className={classnames(styles.toggleSwitch, { [styles.active]: formData.sendImmediately })}
                                    onClick={() => handleInputChange('sendImmediately', !formData.sendImmediately)}
                                >
                                    <div
                                        className={classnames(styles.toggleSwitchHandle, { [styles.active]: formData.sendImmediately })}
                                    />
                                </div>
                            </div>
                        </FormGroup>

                        <FormGroup>
                            <Label>Send at</Label>
                            <DatePicker
                                selected={formData.sendAt}
                                onChange={(date) => handleInputChange('sendAt', date)}
                                showTimeSelect
                                timeFormat="HH:mm"
                                timeIntervals={15}
                                dateFormat="MMMM d, yyyy h:mm aa"
                                minDate={new Date()}
                                disabled={formData.sendImmediately}
                                className="form-control"
                                placeholderText="Select date and time..."
                                filterTime={(time) => {
                                    const selectedDate = new Date(formData.sendAt);
                                    const today = new Date();
                                    if (selectedDate.toDateString() === today.toDateString()) {
                                        return time.getTime() > today.getTime();
                                    }
                                    return selectedDate.getTime() > today.setHours(23, 59, 59, 999);
                                }}
                            />
                        </FormGroup>
                    </div>
                </div>

                <div className="d-flex justify-content-end">
                    <Button color="primary" onClick={handleSubmit} disabled={isLoading}>
                        {isLoading ? 'Sending...' : 'Send Notification'}
                    </Button>
                </div>
            </Col>
        </Row>
    );
}